import { io, Socket } from 'socket.io-client'
import {
  MessagingSocketClientEvent,
  MessagingSocketEvent
} from '@/types/messaging'

type TokenGetter = () => Promise<string>

const MESSAGING_NAMESPACE_PATH = '/messaging'

/**
 * Resolve the base URL the browser should hit for the socket connection.
 * Defaults to same-origin so the request lands on the API gateway, which
 * proxies /socket.io to communication-service. Override with
 * VITE_SOCKET_URL when running a split deployment.
 */
const resolveSocketUrl = (): string => {
  const explicit = import.meta.env.VITE_SOCKET_URL as string | undefined
  if (explicit) return explicit
  if (typeof window !== 'undefined') {
    return window.location.origin
  }
  return 'http://localhost:8080'
}

interface PendingHandler {
  event: MessagingSocketEvent
  handler: (...args: unknown[]) => void
}

class MessagingSocketClient {
  private socket: Socket | null = null
  private tokenGetter: TokenGetter | null = null
  private pendingHandlers: PendingHandler[] = []

  /** Configure how to fetch a fresh Auth0 access token at connect time. */
  setTokenGetter(getter: TokenGetter): void {
    this.tokenGetter = getter
  }

  async connect(): Promise<void> {
    if (this.socket?.connected) return
    if (!this.tokenGetter) {
      throw new Error('messagingSocket: token getter not set')
    }

    const token = await this.tokenGetter()
    const url = resolveSocketUrl()

    this.socket = io(`${url}${MESSAGING_NAMESPACE_PATH}`, {
      transports: ['websocket'],
      auth: { token },
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    })

    for (const { event, handler } of this.pendingHandlers) {
      this.socket.on(event, handler)
    }

    await new Promise<void>((resolve, reject) => {
      const cleanup = (): void => {
        this.socket?.off('connect', onConnect)
        this.socket?.off('connect_error', onError)
      }
      const onConnect = (): void => {
        cleanup()
        resolve()
      }
      const onError = (err: Error): void => {
        cleanup()
        reject(err)
      }
      this.socket!.once('connect', onConnect)
      this.socket!.once('connect_error', onError)
    })
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
    }
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false
  }

  on<T = unknown>(event: MessagingSocketEvent, handler: (payload: T) => void): () => void {
    const wrapped = handler as (...args: unknown[]) => void
    if (this.socket) {
      this.socket.on(event, wrapped)
    } else {
      // Child components mount (and subscribe) before the provider's
      // connect() effect runs, so queue the handler and attach it once
      // the socket is created.
      this.pendingHandlers.push({ event, handler: wrapped })
    }
    return () => {
      this.socket?.off(event, wrapped)
      this.pendingHandlers = this.pendingHandlers.filter(
        (p) => p.event !== event || p.handler !== wrapped
      )
    }
  }

  joinRoom(roomId: string): void {
    this.socket?.emit(MessagingSocketClientEvent.ROOM_JOIN, { roomId })
  }

  leaveRoom(roomId: string): void {
    this.socket?.emit(MessagingSocketClientEvent.ROOM_LEAVE, { roomId })
  }
}

export const messagingSocket = new MessagingSocketClient()
